import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import utils from '@ehi/global-marketing-interface/lib/utils';
import { i18n } from 'utils/i18n';
import { config } from 'utils/config';
import { safeExecute, hasError } from 'utils/validation';
import { scrollLock } from 'utils/scrollLock';
import { isMouseEvent, isTouchEvent } from 'utils/dom';
import { dtm } from 'utils/analytics';
import { pluralHandler } from 'utils/pluralHandler';
import { convertKilometersToMiles } from 'utils/locations';
import { LOCATIONS, RESERVATIONS, MODAL, ANALYTICS, GMI_SERVICE_PATHS } from 'constants';
import Button from 'components/Button';
import FilterToggle from 'components/SortFilterWrapper/FilterToggle';
import StoreFinderNoResults from 'components/StoreFinderNoResults/StoreFinderNoResults';
import NoResultsPartnerDisplay from 'components/NoResultsPartnerDisplay/NoResultsPartnerDisplay';
import LocationMap from 'components/LocationFinder/LocationMap';
import LocationSearch from 'components/LocationSearch';
import PrerateAdditionalInfoModal from 'components/modals/PrerateAdditionalInfoModal';
import BookWithPartnerModal from 'components/modals/BookWithPartnerModal';
import UnavailableLocationModal from 'components/modals/UnavailableLocationModal';
import VehicleClassFilterModal from 'components/modals/VehicleClassFilterModal';
import ServiceErrors from 'components/ServiceErrors';
import LocationTypeFilterModal from 'components/modals/LocationTypeFilterModal';
import GenericNotification from 'components/GenericNotification';
import ClearAllButton from 'components/SortFilterWrapper/ClearAllButton';
import LocationFilters from './LocationFilters';
import MapLocationDetail from './MapLocationDetail';

const { EXCHANGE_TYPE_PICKUP, EXCHANGE_TYPE_RETURN, EXCHANGE_TYPE_ACCESSOR_OBJECT } = RESERVATIONS;

/* *
 *  This component has two main parts the store list (<MapLocationDetail/>) and a map part (<LocationMap/>).
 *  The stores showing in the map are listed in the store list
 *  We have three main states - a selected location, hovered, and expanded state.
 *  A location is selected by clicking either the pin on the map or a store in the store list
 *  This disables the hover state.  To expand a store you must click the details arrow to expand
 *  a store on the store list.  We keep the ID's of the store, which are from GBO/solr in state
 *  here to know which one is hovered, selected, and expanded.
 */

const defaultState = {
  hasAlamoLocations: false,
  hasPartnerLocations: false,
  expandedLocationId: null,
  selectedLocationId: null,
  hoveredLocationId: null,
  filtersExpanded: false,
  autoShowPartnerResults: false,
  showPartnerResultsToggle: false,
  isMapCentered: true,
  isUserDraggedOrZoom: false,
  mobileMapViewFocused: false,
  mobileShowLocationDetails: false,
  allAlamoLocationsSoldOut: false,
  allLocationsSoldOut: false,
  showPartnerResultsAndLocations: false,
};

const LocationFinder = (props) => {
  const {
    selectedLocation,
    setLocation,
    uiPickupLocation,
    showSoldOutNearbyLocations,
    genericTitle,
    isLocationWithNoVehiclesAvailable,
    openSoldOutModal,
    locations,
    reservationFlow,
    updatedSoldOutDateTime,
    adjustLocationId,
    isPickupTimeInThePast,
    isProgressBarExpanded,
    dispatchMobileExpanded,
    dispatchExpandedTab,
    breakpoint,
    isAvailableLocations,
    overlayText,
    fetchLocations,
    storeKey,
    reservationInfo = [],
    showingPartnerLocations,
    setCurrentTimestamp,
    numOfVehicleSelected,
    showPartnerResults,
    composeFilters,
    showReturnLocation,
    updateReservationFlowLocation,
    openModal,
    setLocationDataUnavailableLocationModal,
    resolveBranchURL,
    one_way_rental,
    isInitiateDateTimeisChanged,
    isDateTimeInitiateRequestValues,
    onDateChange,
    onTimeChange,
    gboReservationExists,
    reservationData,
    setPartnerRewardProgram,
    setPartnerRewardProgramMemberId,
    unsetLocation,
    toggleShowReturnLocation,
    locationFilters,
    numFiltersSelected,
    brands_in_result,
    defaultCountry,
    timestamp,
    handleClearAllFilter,
  } = props;

  // Compute a valid center BEFORE the first render
  const initialCenter = useMemo(() => {
    if (selectedLocation?.gps?.latitude && selectedLocation?.gps?.longitude) {
      return {
        lat: Number(selectedLocation.gps.latitude),
        lng: Number(selectedLocation.gps.longitude),
      };
    }

    // Fallback center
    // Default to Chicago (GoogleMapHOC default center)
    return { lat: 41.850033, lng: -87.6500523 };
  }, [selectedLocation, defaultCountry]);

  const [state, setState] = useState(defaultState);
  const [center, setCenter] = useState(initialCenter);
  const [exchangeType, setExchangeType] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [updatedLocationStillSoldOut, setUpdatedLocationStillSoldOut] = useState(false);

  // Destructure state for easier access
  const {
    hasAlamoLocations,
    hasPartnerLocations,
    expandedLocationId,
    selectedLocationId,
    hoveredLocationId,
    filtersExpanded,
    autoShowPartnerResults,
    showPartnerResultsToggle,
    isUserDraggedOrZoom,
    mobileMapViewFocused,
    mobileShowLocationDetails,
    allAlamoLocationsSoldOut,
    allLocationsSoldOut,
    showPartnerResultsAndLocations,
  } = state;

  // Refs for instance variables and previous values
  const markerEventInstanceObjKey = useRef(null); // Google Map specific marker mouse interaction object key
  const prevPropsRef = useRef(props);
  const pageStatus = document.getElementById('pageStatus');

  const getAllLocationsSoldOut = (soldOutEnabled, locationResults) =>
    soldOutEnabled &&
    locationResults.length > 0 &&
    locationResults.every(({ location_availability }) => location_availability === LOCATIONS.UNAVAILABLE_LOCATIONS);

  const getBranches = useCallback(
    (gps, userDraggedOrZoomed = false, radius = 36) => {
      const { latitude, longitude } = gps;
      const { pickupDate, pickupTime, dropoffDate, dropoffTime } = reservationInfo;
      const postalCode = selectedLocation?.address?.postal;
      //  reservationInfo includes dates and times of pickup + return
      fetchLocations(
        gps,
        storeKey,
        pickupDate,
        pickupTime,
        dropoffDate,
        dropoffTime,
        reservationFlow,
        postalCode,
        radius
      ).then((response) => {
        const { brands_in_result: brandsIn_result = [] } = response || {};
        const newHasAlamoLocations = brandsIn_result.indexOf(LOCATIONS.BRAND_ALAMO) !== -1;
        const newHasPartnerLocations = brandsIn_result.length > 1;
        const newAutoShowPartnerResults = !newHasAlamoLocations;
        const newShowPartnerResultsToggle =
          (userDraggedOrZoomed && !newHasAlamoLocations) || (newAutoShowPartnerResults && !showingPartnerLocations);
        const newShowPartnerResultsAndLocations = newAutoShowPartnerResults && showingPartnerLocations;

        if (newAutoShowPartnerResults) {
          // Update redux state when location search results in only partner locations
          showPartnerResults();
        }

        setState((prev) => ({
          ...prev,
          expandedLocationId: defaultState.expandedLocationId,
          hoveredLocationId: defaultState.hoveredLocationId,
          filtersExpanded: numOfVehicleSelected > 0,
          showPartnerResults: defaultState.showPartnerResults,
          autoShowPartnerResults: newAutoShowPartnerResults,
          showPartnerResultsToggle: newShowPartnerResultsToggle,
          isUserDraggedOrZoom: userDraggedOrZoomed,
          hasAlamoLocations: newHasAlamoLocations,
          hasPartnerLocations: newHasPartnerLocations,
          showPartnerResultsAndLocations: newShowPartnerResultsAndLocations,
        }));
        setCenter({ lat: latitude, lng: longitude });
        setCurrentTimestamp();
      });
    },
    [
      fetchLocations,
      storeKey,
      reservationInfo,
      selectedLocation,
      reservationFlow,
      showingPartnerLocations,
      showPartnerResults,
      numOfVehicleSelected,
      setCurrentTimestamp,
    ]
  );

  useEffect(() => {
    const queryParams = utils.getObjFromStrPairs(window.location.search);

    document.title = `${i18n('reservation_flow_title_select_location')} - ${genericTitle}`;

    // assume if we have a longCode query param we should be expecting location data in the query string
    if (!utils.isObjectEmpty(queryParams) && queryParams.longCode) {
      const { longCode, latitude, longitude } = queryParams;
      const locationData = { name: decodeURIComponent(longCode), gps: { latitude, longitude } };

      // dispatch action to set this as the selected location
      setLocation(locationData);

      // In the case a user directly hits the Store Finder page _with_ a location in the query string
      // AND the location is already in session storage (which happens after the first load @see setLocation),
      // we want to fire the `getBranches` function to properly populate the map. This is needed since the initial
      // redux store is hydrated with any data from session storage (@see reducers/locationFinder.js)
      getBranches(latitude && longitude ? locationData.gps : selectedLocation.gps);
    } else if (selectedLocation) {
      // showSoldOutNearbyLocations would be true if it's coming from sold out modal in res
      if (uiPickupLocation && showSoldOutNearbyLocations) {
        getBranches(uiPickupLocation.gps);
      } else {
        getBranches(selectedLocation.gps);
      }
    }

    // To ensure assistive technology users or to notify that the page has been updated, within Reservation flow
    if (pageStatus) {
      pageStatus.setAttribute('aria-live', 'polite');
      pageStatus.innerText = i18n('location_finder_title');
    }

    // Clears the text in the live region after 15 seconds to prevent screen readers from reading outdated information when users navigate to other pages
    setTimeout(() => {
      if (pageStatus) {
        pageStatus.innerText = '';
        pageStatus.setAttribute('aria-live', 'off');
      }
    }, 15000);
  }, []);

  // Effect for componentDidUpdate
  useEffect(() => {
    const prevProps = prevPropsRef.current;

    if (uiPickupLocation && !prevProps.showSoldOutNearbyLocations && showSoldOutNearbyLocations) {
      getBranches(uiPickupLocation.gps);
    } else {
      // compare locations by ID primitive value, not reference to reference
      selectedLocation &&
        !isLocationWithNoVehiclesAvailable &&
        (!prevProps.selectedLocation || selectedLocation.id !== prevProps.selectedLocation.id) &&
        getBranches(selectedLocation.gps);
    }

    if (!prevProps.isLocationWithNoVehiclesAvailable && isLocationWithNoVehiclesAvailable) {
      openSoldOutModal();
    }

    if (locations.length !== prevProps.locations.length) {
      const soldOutEnabled = reservationFlow && config.getIsSoldOutTrueAvailability();

      const computedAllLocationsSoldOut = getAllLocationsSoldOut(soldOutEnabled, locations);

      const computedAllAlamoLocationsSoldOut = getAllLocationsSoldOut(
        soldOutEnabled,
        locations.filter(({ brand }) => brand === LOCATIONS.BRAND_ALAMO)
      );

      const computedUpdatedLocationStillSoldOut =
        updatedSoldOutDateTime &&
        locations.filter(({ id }) => adjustLocationId === id)[0]?.location_availability ===
          LOCATIONS.UNAVAILABLE_LOCATIONS;

      setState((prev) => ({
        ...prev,
        allAlamoLocationsSoldOut: computedAllAlamoLocationsSoldOut,
        allLocationsSoldOut: computedAllLocationsSoldOut,
      }));
      setUpdatedLocationStillSoldOut(computedUpdatedLocationStillSoldOut);
    }

    // On reservation/initiate if the response returns an error with code INVALID_PICK_UP_TIME_IN_THE_PAST, then open Progress Bar.
    if (isPickupTimeInThePast && prevProps.isPickupTimeInThePast !== isPickupTimeInThePast && !isProgressBarExpanded) {
      const { isMobile } = breakpoint;
      dispatchExpandedTab('datesTimes');
      isMobile && dispatchMobileExpanded(isMobile);
    }

    // Adds text to loading-overlay "Finding your locations", while fetching the available location data
    if (!isAvailableLocations) {
      overlayText?.(i18n('finding_your_locations'));
    }

    // Update previous props reference
    prevPropsRef.current = props;
  }, [
    selectedLocation,
    isLocationWithNoVehiclesAvailable,
    showSoldOutNearbyLocations,
    uiPickupLocation,
    locations,
    updatedSoldOutDateTime,
    adjustLocationId,
    isPickupTimeInThePast,
    isAvailableLocations,
  ]);

  //  When the user clicks a map pin or a location detail tile it is considered selected, we use that function for that.
  //  We pass the location's ID, which is the ID of the store coming from Solr/GBO
  const handleSelectedLocation = (locationId) => {
    setState((prev) => ({
      ...prev,
      selectedLocationId: locationId,
    }));
  };

  const setSelectedLocationData = (location, selectedExchangeType) => {
    setCurrentLocation(location);
    setExchangeType(selectedExchangeType);
  };

  /**
   * @private
   * Gets the MouseEvent instance from the gmap marker event object
   */
  const getMarkerMouseEventInstanceObjKey = (e) => {
    const findMouseOrTouchEvent = (key) => key && (isMouseEvent(e[key]) || isTouchEvent(e[key]));
    const [mouseEventKey] = Object.keys(e).filter(findMouseOrTouchEvent);
    return mouseEventKey;
  };

  const handleSelectLocation = (location) => () => {
    if (reservationFlow) {
      const currentExchangeType = showReturnLocation ? EXCHANGE_TYPE_RETURN : EXCHANGE_TYPE_PICKUP;
      //  default errors for modal to false

      const pickupDateAndHoursMessage =
        location.additional_data[EXCHANGE_TYPE_ACCESSOR_OBJECT[EXCHANGE_TYPE_PICKUP]].validityType;
      const returnDateAndHoursMessage =
        location.additional_data[EXCHANGE_TYPE_ACCESSOR_OBJECT[EXCHANGE_TYPE_RETURN]].validityType;

      let pickupDateHoursInvalid = false;
      let returnDateHoursInvalid = false;

      if (!one_way_rental || (one_way_rental && currentExchangeType === EXCHANGE_TYPE_PICKUP)) {
        pickupDateHoursInvalid = !hasError(pickupDateAndHoursMessage);
      }

      if (!one_way_rental || (one_way_rental && currentExchangeType === EXCHANGE_TYPE_RETURN)) {
        returnDateHoursInvalid = !hasError(returnDateAndHoursMessage);
      }

      //  checking if we have an error or can just update reservation
      if (pickupDateHoursInvalid || returnDateHoursInvalid) {
        //  Setting state on reservation flow comp, to show in modal
        const { pickupDate, pickupTime, dropoffDate, dropoffTime } = reservationInfo;
        setLocationDataUnavailableLocationModal({
          location,
          pickupDate,
          pickupTime,
          dropoffDate,
          dropoffTime,
        });
        openModal(MODAL.INVALID_DATE_FOR_RES_FLOW);
        setSelectedLocationData(location, currentExchangeType);
      } else {
        if (isInitiateDateTimeisChanged && isDateTimeInitiateRequestValues) {
          const { pickupDate, pickupTime, returnDate, returnTime } = isDateTimeInitiateRequestValues;
          onDateChange(pickupDate, EXCHANGE_TYPE_PICKUP);
          onTimeChange(pickupTime, EXCHANGE_TYPE_PICKUP);
          onDateChange(returnDate, EXCHANGE_TYPE_RETURN);
          onTimeChange(returnTime, EXCHANGE_TYPE_RETURN);
        }

        if (!gboReservationExists && reservationData?.partner_reward_program_code) {
          const partnerRewardProgram = {
            name: reservationData?.partner_reward_program_name,
            code: reservationData?.partner_reward_program_code,
          };

          setPartnerRewardProgram(partnerRewardProgram);
          setPartnerRewardProgramMemberId(reservationData?.partner_reward_program_member_id);
        }

        updateReservationFlowLocation(location, currentExchangeType);
      }
    } else {
      const { type, id } = location;
      if (type !== LOCATIONS.TYPE_CITY && type !== LOCATIONS.TYPE_GEOLOCATION) {
        resolveBranchURL(id);
        scrollLock({ toggleValue: false });
      }
    }
  };

  const handleExpandLocation = (locationId) => {
    setState((prev) => ({
      ...prev,
      expandedLocationId: locationId,
      mobileMapViewFocused: false,
    }));
    breakpoint.isMobile && scrollLock({ toggleValue: !!locationId });
  };

  // applied to cards and map icons
  const handleMouseEnterLocation = (id) => {
    setState((prev) => ({ ...prev, hoveredLocationId: id }));
  };

  // applied to cards and map icons
  const handleMouseLeaveLocation = () => {
    setState((prev) => ({ ...prev, hoveredLocationId: defaultState.hoveredLocationId }));
  };

  const handleMarkerEvents = (e, locationId) => {
    // If markerEventInstanceObjKey.current is initially null, find the key and retain it in instance
    !markerEventInstanceObjKey.current && (markerEventInstanceObjKey.current = getMarkerMouseEventInstanceObjKey(e));
    // Accessibility - To navigate map with a keyboard for screen reader users
    e.domEvent.type === 'keydown' ? setState((prev) => ({ ...prev, selectedLocationId: locationId })) : null;
    switch (
      e.domEvent.type === 'keydown' ? e.domEvent.type : e[markerEventInstanceObjKey.current].type // Accessibility - to handle KeyboardEvent //e.domEvent.type === 'keydown' ? e.type
    ) {
      case 'mouseover':
      case 'keypress':
        setState((prev) => ({ ...prev, hoveredLocationId: locationId }));
        break;
      case 'mouseout':
        setState((prev) => ({ ...prev, hoveredLocationId: null }));
        break;
      case 'click':
      case 'touchend':
      case 'pointerup':
      case 'keydown': // Accessibility - to make selection on KeyboardEvent
        setState((prev) => ({ ...prev, selectedLocationId: locationId }));
        break;
      default:
    }
  };

  const handleToggleFilterExpanded = () => {
    setState((prev) => ({ ...prev, filtersExpanded: !filtersExpanded }));
  };

  const resetTimeStamp = () => {
    setCurrentTimestamp();
  };

  // by default, only showing ALAMO brand locations
  const handleToggleShowPartnerLocations = () => {
    showPartnerResults();
    composeFilters(storeKey, reservationFlow);
    setCurrentTimestamp();
    setState((prev) => ({ ...prev, showPartnerResultsToggle: false }));
  };

  const handleHidePartnerResultsToggle = () => {
    setState((prev) => ({
      ...prev,
      showPartnerResultsToggle: false,
      showPartnerResultsAndLocations: false,
    }));
  };

  const handleMapPanZoomChange = utils.debounce((bounds) => {
    const { center: mapCenter } = bounds;
    const gps = { latitude: mapCenter.lat(), longitude: mapCenter.lng() };
    getBranches(gps, true); // radius * 0.001 * 0.7 Convert radius meters to kilometers and to 70% of radius
  }, 1200);

  const handleToggleMapViewFocused = () => {
    setState((prev) => ({
      ...prev,
      mobileMapViewFocused: !prev.mobileMapViewFocused,
    }));
  };

  const handleFitBounds = () => {
    // this is specific to locations branch page, to get updated branch locations upon updating the city search
    selectedLocation && getBranches(selectedLocation.gps);
  };

  const handleClearAll = (locationTypeFilter) => (e) => {
    handleClearAllFilter(storeKey, locationTypeFilter);
    safeExecute(resetTimeStamp, props.currentFilters, e);
  };

  /**
   * This is a callback hook for when users toggle the
   * close button of the location details tiles on the map in mobile view
   */
  const handleDetailsTileClose = () => {
    setState((prev) => ({ ...prev, expandedLocationId: null }));
  };

  const calculateDistance = (storeLocation) => {
    const currentLocationGps = selectedLocation?.gps;
    let distance = '';
    // Miles conversion 1km = 0.62137119mi
    const measurement = props.countryOfResidence === 'US' ? 'miles' : 'kilometers';
    if (currentLocationGps && storeLocation) {
      // Determine miles conversion
      distance = utils.getKilometersBetweenPoints(currentLocationGps, storeLocation);
      if (measurement === 'miles') {
        distance = convertKilometersToMiles(distance);
      }
      // Determine rounding and display rules
      // additional number coercion to avoid situations like 3.0
      // https://stackoverflow.com/a/18358056/3352956
      distance = distance < 10 ? +distance.toFixed(1) : +distance.toFixed(0);
    }
    return i18n('map_location_detail_tile_distance_label', [distance, measurement]);
  };

  const renderControls = useCallback(
    (allLocationsSoldOutStatus, showAdjustedSoldOut) => {
      const isSmall = breakpoint.isMobile;
      const onHandleToggleShowPartnerLocations = handleToggleShowPartnerLocations;
      const onHandleHidePartnerResultsToggle = handleHidePartnerResultsToggle;
      // On clicking the `Recenter Map` pill button (located on the map),
      // `showPartnerResultsToggle` reset to false hence user are not able see `NoResultsPartnerDisplay/StoreFinderNoResults` component,
      // creating state `showPartnerResultsAndLocations` to display `NoResultsPartnerDisplay/StoreFinderNoResults` component
      // if `autoShowPartnerResults && showingPartnerLocations` are true
      const filterTitle = `${locations.length} ${pluralHandler(
        locations.length,
        'location_tiles_header_singular',
        'location_tiles_header_plural',
        true
      )}`;
      let title = 'location_finder_title';

      if (reservationFlow && one_way_rental) {
        title = showReturnLocation
          ? 'location_finder_choose_return_location'
          : 'location_finder_choose_pickup_location';
      }

      return (
        <>
          <div className='location-store-finder__controls location-store-finder__list-width'>
            {showReturnLocation && (
              <Button
                link
                data-dtm-track={dtm(ANALYTICS.UI_BUTTON, 'back_to_pickup', ANALYTICS.BACK)}
                className='link--arrow-reverse'
                onClick={toggleShowReturnLocation}
              >
                {i18n('location_finder_back_to_pickup_location')}
              </Button>
            )}
            <div className='location-store-finder__heading'>
              {!props.showLocationSearch && (
                <ServiceErrors statePath={GMI_SERVICE_PATHS.RESERVATIONS_INITIATE} runAllHandlers />
              )}
              <h1 className='location-store-finder__title'>{i18n(title)}</h1>
              {!isSmall && (
                <div className='location-store-finder__filterToggle'>
                  {(numFiltersSelected > 0 || numOfVehicleSelected > 0) && (
                    <ClearAllButton hideDesktopHeader handleClearAll={handleClearAll('locationTypeFilter')} />
                  )}
                  <FilterToggle
                    showVehicleCount={true}
                    storeKey={storeKey}
                    open={filtersExpanded}
                    onToggle={handleToggleFilterExpanded}
                  />
                </div>
              )}
            </div>
            {!isSmall && (
              <LocationFilters
                open={filtersExpanded}
                filterConfig={locationFilters}
                resultsCount={locations.length}
                storeKey={storeKey}
                onClose={handleToggleFilterExpanded}
                onChange={resetTimeStamp}
                locations={locations}
              />
            )}
            {props.showLocationSearch && (
              <LocationSearch
                name='location-search'
                selectedLocation={selectedLocation}
                setLocation={setLocation}
                unsetLocation={unsetLocation}
                type={LOCATIONS.LOCATION_SEARCH_TYPES.LOCATION_FINDER}
              />
            )}

            <LocationTypeFilterModal
              filterConfig={locationFilters}
              resultsCount={locations.length}
              storeKey={storeKey}
              onChange={resetTimeStamp}
            />
            <VehicleClassFilterModal onChange={resetTimeStamp} />
          </div>

          {!breakpoint.isTabletOrMobile && allLocationsSoldOutStatus && !showAdjustedSoldOut && (
            <GenericNotification
              messageClass='generic-notification__sold-out-icon'
              bannerClass='generic-notification__sold-out'
              title={i18n('common_sold_out').toUpperCase()}
              dtmTrack={dtm(ANALYTICS.LOCATION_PAGE, ANALYTICS.SOLD_OUT, ANALYTICS.UI_BANNER)}
              message={hasPartnerLocations ? i18n('sold_out_area_partner_locations') : i18n('sold_out_area')}
            />
          )}

          {!breakpoint.isTabletOrMobile && showAdjustedSoldOut && (
            <GenericNotification
              messageClass='generic-notification__no-icon'
              bannerClass='generic-notification__sold-out'
              message={i18n('adjusted_location_sold_out')}
            />
          )}

          {!hasAlamoLocations && (showPartnerResultsToggle || showPartnerResultsAndLocations) && (
            <div className='location-store-finder__list-width'>
              {locations.length ? (
                <NoResultsPartnerDisplay
                  shouldShowCloseButton={showingPartnerLocations}
                  handleToggleShowPartnerLocations={onHandleToggleShowPartnerLocations}
                  handleHidePartnerResultsToggle={onHandleHidePartnerResultsToggle}
                />
              ) : (
                <StoreFinderNoResults
                  isUserDraggedOrZoom={isUserDraggedOrZoom}
                  isReservationFlow={reservationFlow}
                  setMobileExpanded={dispatchMobileExpanded}
                  setExpandedTab={dispatchExpandedTab}
                  isProgressBarExpanded={isProgressBarExpanded}
                  breakpoint={breakpoint}
                />
              )}
            </div>
          )}

          {isSmall &&
            isAvailableLocations &&
            (hasAlamoLocations || showingPartnerLocations || autoShowPartnerResults) && (
              <>
                <div className='map-locations__heading location-store-finder__list-width'>
                  <span>{!!locations.length && filterTitle}</span>
                  <div className='location-store-finder__filterToggle'>
                    {(numFiltersSelected > 0 || numOfVehicleSelected > 0) && (
                      <ClearAllButton hideDesktopHeader handleClearAll={handleClearAll('locationTypeFilter')} />
                    )}
                    <FilterToggle
                      showVehicleCount={true}
                      storeKey={storeKey}
                      open={filtersExpanded}
                      onToggle={handleToggleFilterExpanded}
                    />
                  </div>
                </div>
                <LocationFilters
                  open={filtersExpanded}
                  filterConfig={locationFilters}
                  resultsCount={locations.length}
                  storeKey={storeKey}
                  onClose={handleToggleFilterExpanded}
                  onChange={resetTimeStamp}
                  locations={locations}
                />
              </>
            )}

          {!isSmall &&
            !!locations.length &&
            (hasAlamoLocations || showingPartnerLocations || autoShowPartnerResults) && (
              <div className='map-locations__heading location-store-finder__list-width'>
                <span>{filterTitle}</span>
              </div>
            )}
        </>
      );
    },
    [
      locations,
      showingPartnerLocations,
      hasAlamoLocations,
      autoShowPartnerResults,
      showPartnerResultsToggle,
      hasPartnerLocations,
      showPartnerResultsAndLocations,
      filtersExpanded,
    ]
  );

  // Render logic
  const isMediumDown = breakpoint.isTabletOrMobile;
  //  Decides when a map tile should be show, on the map view - only happens when it is on a tablet screen or down + has been selected
  //  to deselect we click the map (not the map pins, that would select one)
  const showLocationDetailTile = isMediumDown && selectedLocationId !== null;
  const mapLocationsToDisplay =
    hasAlamoLocations || (isUserDraggedOrZoom && showingPartnerLocations) || autoShowPartnerResults ? locations : [];

  const soldOutEnabled = reservationFlow && config.getIsSoldOutTrueAvailability();
  const showAdjustedSoldOut = soldOutEnabled && updatedLocationStillSoldOut;

  return (
    <div className='location-store-finder'>
      <UnavailableLocationModal
        showReturnLocation={showReturnLocation}
        updateReservationFlowLocation={updateReservationFlowLocation}
        locationName={currentLocation && currentLocation.name}
        locationIdSelectedInUnavailableLocationModal={currentLocation && currentLocation.id}
        location={currentLocation}
        exchangeType={exchangeType}
        one_way_rental={one_way_rental}
      />

      {isMediumDown && renderControls(allLocationsSoldOut, showAdjustedSoldOut)}
      <div
        className={cn('location-store-finder__content', {
          'map-focused': mobileMapViewFocused,
          'show-details': mobileShowLocationDetails,
        })}
      >
        <div className='location-store-finder__location-display-section'>
          {!isMediumDown && renderControls(allLocationsSoldOut, showAdjustedSoldOut)}
          <MapLocationDetail
            locations={locations}
            brandsInResult={brands_in_result}
            hasAlamoLocations={hasAlamoLocations}
            hasPartnerLocations={hasPartnerLocations}
            expandedLocationId={expandedLocationId}
            handleSelectedLocation={handleSelectedLocation}
            selectedLocationId={selectedLocationId}
            hoveredLocationId={hoveredLocationId}
            showReturnLocation={showReturnLocation}
            reservationFlow={reservationFlow}
            onSelectLocation={handleSelectLocation}
            onExpandLocation={handleExpandLocation}
            onMouseEnterLocation={handleMouseEnterLocation}
            onMouseLeaveLocation={handleMouseLeaveLocation}
            onHandleToggleShowPartnerLocations={handleToggleShowPartnerLocations}
            showPartnerResults={showingPartnerLocations}
            autoShowPartnerResults={autoShowPartnerResults}
            mobileMapViewFocused={mobileMapViewFocused}
            selectedLocation={selectedLocation}
            calculateDistance={calculateDistance}
            resetTimeStamp={resetTimeStamp}
            allLocationsSoldOut={allLocationsSoldOut}
            allAlamoLocationsSoldOut={allAlamoLocationsSoldOut}
            breakpoint={breakpoint}
            showAdjustedSoldOut={showAdjustedSoldOut}
          />
        </div>
        <div className='location-store-finder__map-display-section'>
          <LocationMap
            resetTimeStamp={resetTimeStamp}
            breakpoint={breakpoint}
            locations={mapLocationsToDisplay}
            selectedLocation={selectedLocation}
            handleSelectedLocation={handleSelectedLocation}
            selectedLocationId={selectedLocationId}
            hoveredLocationId={hoveredLocationId}
            showCurrentLocation
            isUserDraggedOrZoom={isUserDraggedOrZoom}
            zoom={10}
            center={center}
            defaultCountry={defaultCountry}
            showLocationDetailTile={showLocationDetailTile}
            onSelectLocation={handleSelectLocation}
            onExpandLocation={handleExpandLocation}
            onMarkerEvents={handleMarkerEvents}
            onDragEnd={handleMapPanZoomChange}
            onZoomChanged={handleMapPanZoomChange}
            onFitBounds={handleFitBounds}
            onDetailsTileClose={handleDetailsTileClose}
            timestamp={timestamp}
            isVisible={isMediumDown ? mobileMapViewFocused : true}
            isReservationFlow={reservationFlow}
            setMobileExpanded={dispatchMobileExpanded}
            setExpandedTab={dispatchExpandedTab}
          />
        </div>
      </div>

      {isMediumDown && (
        <Button
          data-dtm-track={dtm(ANALYTICS.UI_BUTTON, 'location', mobileMapViewFocused ? 'listicon' : 'mapicon')}
          className={cn('location-store-finder__map-toggle pill', { 'map-focused': mobileMapViewFocused })}
          onClick={handleToggleMapViewFocused}
        >
          {mobileMapViewFocused ? i18n('list') : i18n('map')}
        </Button>
      )}

      <PrerateAdditionalInfoModal />
      <BookWithPartnerModal />
    </div>
  );
};

LocationFinder.propTypes = {
  locations: PropTypes.array,
  showLocationSearch: PropTypes.bool,
  openModal: PropTypes.func.isRequired,
  one_way_rental: PropTypes.bool,
  setReservationError: PropTypes.func,
  setSelectedLocationData: PropTypes.func,
  breakpoint: PropTypes.object,
  isLocationWithNoVehiclesAvailable: PropTypes.bool,
  openSoldOutModal: PropTypes.func,
  showSoldOutNearbyLocations: PropTypes.bool,
  uiPickupLocation: PropTypes.object,
  uiReturnLocation: PropTypes.object,
  dispatchShowSoldOutNearbyLocations: PropTypes.func,
  updateReservationFlowLocation: PropTypes.func.isRequired,
  dispatchMobileExpanded: PropTypes.func,
  dispatchExpandedTab: PropTypes.func,
  adjustLocationId: PropTypes.string,
  composeFilters: PropTypes.func,
  updatedSoldOutDateTime: PropTypes.bool,
  showVehicleClassFilterModal: PropTypes.func,
  showLocationTypeFilterModal: PropTypes.func,
  overlayText: PropTypes.func,
  isAvailableLocations: PropTypes.bool,
  handleClearAllFn: PropTypes.func,
};

LocationFinder.defaultProps = {
  locations: {},
  showLocationSearch: false,
};

export default LocationFinder;
